import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { FiRepeat } from "react-icons/fi";
import { RRule } from "rrule";
import { modal } from "../../../util/useModal";
import AppCalendar from "../../../components/AppCalendar/AppCalendar";
import KitButton from "../../../components/kit/Button";
import {
  formatCalendarDateTime,
  getCalendarStatusClass,
} from "../../../components/AppCalendar/calendarUtils";
import LineupManager from "../../../components/LineupTab/LineupManager";
import EventPostingSurface from "../new-event/EventPostingSurface";

const DEFAULT_POSTING_HOUR = 19;
const DEFAULT_POSTING_DURATION_MS = 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

const toLocalDateTimeString = (date) => {
  const value = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(value.getTime())) return "";

  const pad = (part) => String(part).padStart(2, "0");
  return [
    value.getFullYear(),
    pad(value.getMonth() + 1),
    pad(value.getDate()),
  ].join("-") + `T${pad(value.getHours())}:${pad(value.getMinutes())}`;
};

const getLocalDayStart = (date) => {
  const value = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(value.getTime())) return null;
  return new Date(value.getFullYear(), value.getMonth(), value.getDate());
};

const getMultiDaySelectionSpan = ({ start, end, allDay }) => {
  if (!end) return null;

  const selectionStart = new Date(start);
  const selectionEnd = new Date(end);
  const startDay = getLocalDayStart(selectionStart);
  const endDay = getLocalDayStart(selectionEnd);

  if (!startDay || !endDay) return null;

  const dayDiff = Math.round((endDay.getTime() - startDay.getTime()) / DAY_MS);

  if (allDay) {
    if (dayDiff <= 1) return null;

    // FullCalendar all-day selection end is exclusive.
    const lastSelectedDay = new Date(endDay);
    lastSelectedDay.setDate(lastSelectedDay.getDate() - 1);

    return {
      dayCount: dayDiff,
      lastSelectedDay,
    };
  }

  // Keep normal overnight selections as one event; only long timed selections
  // should become daily recurring drafts.
  if (dayDiff < 1 || selectionEnd.getTime() - selectionStart.getTime() < DAY_MS) {
    return null;
  }

  return {
    dayCount: dayDiff + 1,
    lastSelectedDay: endDay,
  };
};

const buildDailySelectionRecurrence = (range, span) => {
  const until = new Date(span.lastSelectedDay);
  until.setHours(23, 59, 0, 0);

  return {
    origOptions: {
      freq: RRule.DAILY,
      interval: 1,
      dtstart: toLocalDateTimeString(range.start),
      until: toLocalDateTimeString(until),
      tzid: "utc",
    },
  };
};

const formatDailySelectionSummary = (range, span) => {
  const firstOccurrence = formatCalendarDateTime(range.start, range.end);
  const lastDay = span.lastSelectedDay.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });

  return `${firstOccurrence} · daily through ${lastDay}`;
};

const normalizePostingRange = ({ start, end, allDay }) => {
  const normalizedStart = new Date(start);
  if (allDay) {
    normalizedStart.setHours(DEFAULT_POSTING_HOUR, 0, 0, 0);
  }

  let normalizedEnd =
    end && !allDay
      ? new Date(end)
      : new Date(normalizedStart.getTime() + DEFAULT_POSTING_DURATION_MS);

  if (Number.isNaN(normalizedEnd.getTime())) {
    normalizedEnd = new Date(normalizedStart.getTime() + DEFAULT_POSTING_DURATION_MS);
  } else if (normalizedEnd <= normalizedStart) {
    normalizedEnd = new Date(normalizedEnd);
    normalizedEnd.setDate(normalizedEnd.getDate() + 1);
  }

  return {
    start: normalizedStart,
    end: normalizedEnd,
  };
};

const buildPostingDraft = (selection) => {
  const range = normalizePostingRange(selection);
  const multiDaySpan = getMultiDaySelectionSpan(selection);
  const dailyRecurrence = multiDaySpan
    ? buildDailySelectionRecurrence(range, multiDaySpan)
    : null;

  return {
    date: {
      start: toLocalDateTimeString(range.start),
      end: toLocalDateTimeString(range.end),
    },
    rrule: dailyRecurrence,
    _calendarSelectionSummary: multiDaySpan
      ? formatDailySelectionSummary(range, multiDaySpan)
      : undefined,
    _calendarSelectionDayCount: multiDaySpan?.dayCount,
  };
};

const PostedEventsCalendar = ({
  scope = "all",
  rosterId,
  rosterName,
  calendarEvents,
  calendarView,
  setCalendarRange,
  setCalendarView,
  hasNoCalendarMatches,
  filtersCollapsed,
  setFiltersCollapsed,
  activeFilterCount,
  activeFilterSummary,
  applyNeedsAttentionPreset,
  hasCustomFilters,
  clearCalendarFilters,
  statusFilters,
  enabledStatuses,
  toggleStatusFilter,
  getFilterButtonClass,
  lineupFilters,
  enabledLineupFilters,
  toggleLineupFilter,
  applicantLoadingByEvent,
  condenseRejectedRecurring,
  setCondenseRejectedRecurring,
  eventSearchTerm,
  setEventSearchTerm,
  selectedParentEvent,
  selectedOccurrenceDisplayStart,
  selectedOccurrenceDisplayEnd,
  selectedCalendarProps,
  selectedSlotSummary,
  selectedApplicantSummary,
  isApplicantsLoading,
  handleCalendarEventClick,
  calendarFocusDate,
  closeCalendarPanel,
  openEventDetails,
  cancelEvent,
  handlePopupLineupChange,
  userLocation,
  onPostedEventCreated,
  onOpenList,
  listEventCount = 0,
  editingEventRequest,
  compactFilters = false,
  hideToolsPanel = false,
  guidanceCopy,
  // the workspace shows the hint itself (bookings → dates row with the calendar | list toggle)
  hideGuidance = false,
  emptyCopy,
}) => {
  const [postingDraft, setPostingDraft] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [composerDate, setComposerDate] = useState(null);
  const [composerDirty, setComposerDirty] = useState(false);

  const postingDateSummary = useMemo(() => {
    const date = composerDate || postingDraft?.date;
    if (!date) return "";
    if (!composerDate && postingDraft?._calendarSelectionSummary) {
      return postingDraft._calendarSelectionSummary;
    }
    return formatCalendarDateTime(date.start, date.end);
  }, [composerDate, postingDraft]);
  const composerData = editingEvent || postingDraft;
  const isRosterScope = scope === "roster" && rosterId;
  const isBookingsScope = scope === "bookings";
  const composerTitle = editingEvent
    ? "edit event"
    : isRosterScope
    ? `post for ${rosterName || "this roster"}`
    : isBookingsScope
    ? "create booking event"
    : "post from calendar";
  const composerSummary = editingEvent?.name || postingDateSummary;

  const openPostingDraft = (selection) => {
    closeCalendarPanel();
    setEditingEvent(null);
    setComposerDate(null);
    setComposerDirty(false);
    setPostingDraft({
      ...buildPostingDraft(selection),
      ...(isRosterScope ? { roster: rosterId } : {}),
    });
  };

  const closeComposer = () => {
    setPostingDraft(null);
    setEditingEvent(null);
    setComposerDate(null);
    setComposerDirty(false);
  };

  const requestCloseComposer = async () => {
    if (!composerDirty) {
      closeComposer();
      return;
    }

    const shouldDiscard = await modal(({ proceed }) => (
      <div className="max-w-[min(88vw,380px)] text-center">
        <div className="font-['Primary-Bold'] text-kit-lg text-[var(--inyc-primary-text-color)]">
          discard unsaved changes?
        </div>
        <div className="mt-kit-10 text-kit-sm leading-[1.45] text-[var(--inyc-primary-text-color-light)]">
          your changes have not been submitted yet.
        </div>
        <div className="mt-kit-20 flex justify-center gap-kit-12">
          <button
            type="button"
            onClick={() => proceed(false)}
            className="rounded-kit-full border-[1px] border-[var(--inyc-input-border-color)] px-kit-16 py-kit-8 font-['Primary-Medium'] text-kit-sm text-[var(--inyc-primary-text-color)]"
          >
            stay
          </button>
          <button
            type="button"
            onClick={() => proceed(true)}
            className="rounded-kit-full bg-danger-strong px-kit-16 py-kit-8 font-['Primary-Medium'] text-kit-sm text-on-fill"
          >
            discard
          </button>
        </div>
      </div>
    ), { hideCloseButton: true });

    if (shouldDiscard) closeComposer();
  };

  const openEditingEvent = (event) => {
    closeCalendarPanel();
    setPostingDraft(null);
    setComposerDate(null);
    setComposerDirty(false);
    setEditingEvent(event);
  };

  const handleComposerDateChange = useCallback((date) => {
    if (!date) {
      setComposerDate(null);
      return;
    }

    const sourceDate = (editingEvent || postingDraft)?.date;
    const isOriginalDate =
      sourceDate?.start === date.start && sourceDate?.end === date.end;
    setComposerDate(isOriginalDate ? null : date);
  }, [editingEvent, postingDraft]);

  useEffect(() => {
    if (!editingEventRequest?.event) return;
    openEditingEvent(editingEventRequest.event);
  }, [editingEventRequest]);

  return (
    <>
      <div className="w-full max-w-full overflow-hidden rounded-kit-lg border-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary)] p-kit-12 md:p-kit-24">
        {!hideToolsPanel && (
          <div className={`mb-kit-16 rounded-kit-lg border-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary-light)] p-kit-16 text-kit-2xs lowercase text-[var(--inyc-primary-text-color-light)] ${compactFilters ? "opacity-90" : ""}`}>
            <div className={`flex flex-wrap items-center justify-between gap-kit-10 ${filtersCollapsed ? "" : "mb-kit-12"}`}>
              <div className="min-w-0 flex-1">
                <div className="font-['Primary-Bold'] text-kit-xs uppercase tracking-[0.14em] text-[var(--inyc-primary-text-color)]">
                  {compactFilters ? "calendar tools" : "filters"}
                </div>
                <div className="mt-kit-2 text-kit-2xs text-[var(--inyc-primary-text-color-light)]">
                  {activeFilterCount} active
                </div>
                <div className="mt-kit-4 break-words text-kit-xs leading-[1.25] text-[var(--inyc-primary-text-color)]">
                  {activeFilterSummary}
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-end gap-kit-8">
                {onOpenList && (
                  <button
                    type="button"
                    onClick={onOpenList}
                    className="rounded-kit-full border-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary)] px-kit-12 py-kit-6 font-['Primary-Medium'] text-[var(--inyc-primary-text-color)] duration-200 hover:border-[var(--inyc-secondary)]"
                  >
                    list {listEventCount > 0 ? `(${listEventCount})` : ""}
                  </button>
                )}
                <button
                  type="button"
                  onClick={applyNeedsAttentionPreset}
                  className={`rounded-kit-full border-[1px] border-[var(--inyc-secondary)] bg-[var(--inyc-input-background-color)] px-kit-12 py-kit-6 font-['Primary-Medium'] text-[var(--inyc-primary-text-color)] duration-200 hover:bg-[var(--inyc-secondary)] hover:text-[var(--inyc-secondary-text-color)] ${compactFilters ? "hidden sm:inline-flex" : ""}`}
                >
                  needs attention
                </button>
                {hasCustomFilters && (
                  <button
                    type="button"
                    onClick={clearCalendarFilters}
                    className="rounded-kit-full border-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary)] px-kit-12 py-kit-6 font-['Primary-Medium'] text-[var(--inyc-primary-text-color)] duration-200 hover:border-[var(--inyc-secondary)]"
                  >
                    clear filters
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setFiltersCollapsed((prev) => !prev)}
                  className="rounded-kit-full border-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary)] px-kit-12 py-kit-6 font-['Primary-Medium'] text-[var(--inyc-primary-text-color)] duration-200 hover:border-[var(--inyc-secondary)]"
                >
                  {filtersCollapsed ? "show filters" : "hide filters"}
                </button>
              </div>
            </div>

            {!filtersCollapsed && (
              <div className="grid gap-kit-12 lg:grid-cols-[1fr_1.15fr]">
                <div>
                  <div className="mb-kit-6 font-['Primary-Medium'] uppercase tracking-[0.12em]">
                    status
                  </div>
                  <div className="flex flex-wrap items-center gap-kit-8">
                    {statusFilters.map((status) => {
                      const selected = enabledStatuses.includes(status.id);

                      return (
                        <button
                          key={status.id}
                          type="button"
                          onClick={() => toggleStatusFilter(status.id)}
                          className={`inline-flex items-center gap-kit-6 rounded-kit-full border-[1px] px-kit-12 py-kit-6 font-['Primary-Medium'] duration-200 ${getFilterButtonClass({
                            selected,
                            tone: status.id,
                          })}`}
                        >
                          <span>{status.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <div className="mb-kit-6 font-['Primary-Medium'] uppercase tracking-[0.12em]">
                    lineup
                  </div>
                  <div className="flex flex-wrap items-center gap-kit-8">
                    {lineupFilters.map((filter) => {
                      const selected = enabledLineupFilters.includes(filter.id);
                      const loading =
                        filter.id === "pending-applicants" &&
                        selected &&
                        Object.values(applicantLoadingByEvent).some(Boolean);

                      return (
                        <button
                          key={filter.id}
                          type="button"
                          onClick={() => toggleLineupFilter(filter.id)}
                          className={`inline-flex items-center gap-kit-6 rounded-kit-full border-[1px] px-kit-12 py-kit-6 font-['Primary-Medium'] duration-200 ${getFilterButtonClass({
                            selected,
                          })}`}
                        >
                          <span>{filter.label}</span>
                          {loading && <span className="opacity-70">loading</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <div className="mb-kit-6 font-['Primary-Medium'] uppercase tracking-[0.12em]">
                    recurrence
                  </div>
                  <div className="flex flex-wrap items-center gap-kit-8">
                    <button
                      type="button"
                      onClick={() => setCondenseRejectedRecurring((prev) => !prev)}
                      className={`inline-flex items-center gap-kit-6 rounded-kit-full border-[1px] px-kit-12 py-kit-6 font-['Primary-Medium'] duration-200 ${getFilterButtonClass({
                        selected: condenseRejectedRecurring,
                      })}`}
                    >
                      <FiRepeat />
                      <span>{condenseRejectedRecurring ? "condense rejected" : "show all rejected"}</span>
                    </button>
                    <div className="inline-flex items-center gap-kit-6 rounded-kit-full border-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary)] px-kit-12 py-kit-6">
                      <FiRepeat />
                      <span>recurring marker</span>
                    </div>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="posted-events-calendar-search"
                    className="mb-kit-6 block font-['Primary-Medium'] uppercase tracking-[0.12em]"
                  >
                    search
                  </label>
                  <input
                    id="posted-events-calendar-search"
                    value={eventSearchTerm}
                    onChange={(event) => setEventSearchTerm(event.target.value)}
                    placeholder="search by event name"
                    className="w-full rounded-kit-full border-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary)] px-kit-16 py-kit-10 font-['Primary-Regular'] text-kit-xs text-[var(--inyc-primary-text-color)] outline-none placeholder:text-[var(--inyc-primary-text-color-light)]"
                  />
                </div>
              </div>
            )}
          </div>
        )}

        <div className="sr-only" aria-live="polite">
          showing {enabledStatuses.map((status) => status.replace("-", " ")).join(", ")} events
        </div>
        {/* bookings (hideToolsPanel) shows no empty-state box */}
        {hasNoCalendarMatches && !(hideToolsPanel && isBookingsScope) && (
          <div className="mb-kit-16 rounded-kit-lg border-[1px] border-dashed border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary-light)] px-kit-16 py-kit-20 text-center">
            <div className="font-['Primary-Bold'] text-kit-base lowercase text-[var(--inyc-primary-text-color)]">
              {isRosterScope
                ? "no roster events match these filters"
                : "no events match these filters"}
            </div>
            <div className="mt-kit-4 text-kit-xs lowercase text-[var(--inyc-primary-text-color-light)]">
              {emptyCopy ||
                (isRosterScope
                  ? "try clearing filters or click a date to create an event for this roster"
                  : "try clearing filters or adjusting search, or click a date to post something new")}
            </div>
            {!hideToolsPanel && (
              <button
                type="button"
                onClick={clearCalendarFilters}
                className="mt-kit-12 rounded-kit-full bg-[var(--inyc-secondary)] px-kit-16 py-kit-8 font-['Primary-Medium'] text-kit-xs lowercase text-[var(--inyc-secondary-text-color)]"
              >
                clear filters
              </button>
            )}
          </div>
        )}
        {!hideGuidance && (
          <div className="mb-kit-12 rounded-kit-lg border-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary-light)] px-kit-16 py-kit-12 text-kit-xs lowercase text-[var(--inyc-primary-text-color-light)]">
            {guidanceCopy ||
              (isRosterScope
                ? `click a day or drag a time range to create an event for ${rosterName || "this roster"}`
                : "click a day or drag a time range to post a new event from the calendar")}
          </div>
        )}
        <AppCalendar
          events={calendarEvents}
          initialView={calendarView}
          focusDate={calendarFocusDate}
          onRangeChange={setCalendarRange}
          onEventClick={handleCalendarEventClick}
          onViewChange={setCalendarView}
          onDateClick={({ date, allDay }) => openPostingDraft({ start: date, allDay })}
          onDateSelect={({ start, end, allDay, view }) => {
            openPostingDraft({ start, end, allDay });
            view?.calendar?.unselect?.();
          }}
          selectable
        />
      </div>

      {selectedParentEvent && createPortal(
        <>
          <div
            className="fixed inset-0 z-[9998] bg-black/20 md:bg-transparent"
            onClick={closeCalendarPanel}
          />
          <div className="fixed bottom-[var(--inyc-bottom-navbar-height)] left-0 right-0 z-[9999] max-h-[calc(86vh_-_var(--inyc-bottom-navbar-height))] overflow-auto rounded-t-kit-xl border-t-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary)] p-kit-20 pb-kit-20 shadow-[0_-18px_60px_rgba(0,0,0,0.22)] md:bottom-auto md:left-auto md:right-[2rem] md:top-[6rem] md:w-[460px] md:rounded-kit-lg md:border-[1px] md:pb-kit-20">
            <div className="flex items-start justify-between gap-kit-16">
              <div className="min-w-0">
                <div className="text-kit-2xs uppercase tracking-[0.14em] text-[var(--inyc-primary-text-color-light)]">
                  posted event
                </div>
                <h2 className="mt-kit-6 line-clamp-2 font-['Primary-Bold'] text-kit-md leading-[1.2] text-[var(--inyc-primary-text-color)]">
                  {selectedParentEvent.name}
                </h2>
              </div>
              <KitButton variant="tertiary" size="sm" onClick={closeCalendarPanel}>
                close
              </KitButton>
            </div>

            <div className="mt-kit-16 grid grid-cols-3 gap-kit-10">
              <button
                type="button"
                onClick={() => openEditingEvent(selectedParentEvent)}
                className="rounded-kit-full bg-[var(--inyc-secondary)] px-kit-16 py-kit-12 font-['Primary-Medium'] text-[var(--inyc-secondary-text-color)]"
              >
                edit
              </button>
              <button
                type="button"
                onClick={() => openEventDetails(selectedParentEvent)}
                className="rounded-kit-full border-[1px] border-[var(--inyc-input-border-color)] px-kit-16 py-kit-12 font-['Primary-Medium'] text-[var(--inyc-primary-text-color)]"
              >
                open
              </button>
              <button
                type="button"
                onClick={async () => {
                  const cancelled = await cancelEvent(selectedParentEvent);
                  if (cancelled) closeCalendarPanel();
                }}
                className="rounded-kit-full border-[1px] border-danger-line px-kit-16 py-kit-12 font-['Primary-Medium'] text-danger"
              >
                cancel
              </button>
            </div>

            <div className="mt-kit-16 space-y-[0.65rem] rounded-kit-md bg-[var(--inyc-primary-light)] p-kit-16 text-kit-sm">
              <div>
                <div className="text-kit-2xs uppercase tracking-[0.12em] text-[var(--inyc-primary-text-color-light)]">
                  time
                </div>
                <div className="font-['Primary-Medium'] text-[var(--inyc-primary-text-color)]">
                  <span className="inline-flex items-center gap-kit-8">
                    {selectedCalendarProps.isRecurring && (
                      <FiRepeat className="text-[var(--inyc-primary-text-color-light)]" />
                    )}
                    {formatCalendarDateTime(
                      selectedOccurrenceDisplayStart,
                      selectedOccurrenceDisplayEnd
                    )}
                  </span>
                </div>
              </div>
              <div>
                <div className="text-kit-2xs uppercase tracking-[0.12em] text-[var(--inyc-primary-text-color-light)]">
                  status
                </div>
                <div
                  className={`font-['Primary-Medium'] ${
                    getCalendarStatusClass(selectedParentEvent.status) === "rejected"
                      ? "text-danger"
                      : getCalendarStatusClass(selectedParentEvent.status) === "awaiting-review"
                      ? "text-warning"
                      : "text-[var(--inyc-primary-text-color)]"
                  }`}
                >
                  {selectedParentEvent.status?.replaceAll("_", " ") || "not set"}
                </div>
              </div>
              {selectedCalendarProps.isRecurring && (
                <div>
                  <div className="text-kit-2xs uppercase tracking-[0.12em] text-[var(--inyc-primary-text-color-light)]">
                    recurrence
                  </div>
                  <div className="font-['Primary-Medium'] text-[var(--inyc-primary-text-color)]">
                    {selectedCalendarProps.recurrenceText || "repeats"}
                  </div>
                  {selectedCalendarProps.isCondensedRejectedRecurring && (
                    <div className="mt-kit-4 text-kit-xs text-danger">
                      rejected recurring event shown once
                    </div>
                  )}
                </div>
              )}
              {selectedSlotSummary.hasSlots && (
                <div>
                  <div className="text-kit-2xs uppercase tracking-[0.12em] text-[var(--inyc-primary-text-color-light)]">
                    slots
                  </div>
                  <div className="font-['Primary-Medium'] text-[var(--inyc-primary-text-color)]">
                    {selectedSlotSummary.openSlotCount} open /{" "}
                    {selectedSlotSummary.filledSlotCount} filled /{" "}
                    {selectedSlotSummary.slotCount} total
                  </div>
                  <div className="mt-kit-4 text-kit-xs text-[var(--inyc-primary-text-color-light)]">
                    {isApplicantsLoading
                      ? "loading applicant counts..."
                      : selectedApplicantSummary?.error
                      ? selectedApplicantSummary.error
                      : selectedApplicantSummary
                      ? `${selectedApplicantSummary.pendingApplicants} pending applicants, ${selectedApplicantSummary.acceptedApplicants} accepted`
                      : "applicant counts unavailable"}
                  </div>
                </div>
              )}
            </div>

            {selectedSlotSummary.hasSlots && (
              <div className="mt-kit-16">
                <div className="mb-kit-10 flex items-center justify-between">
                  <div>
                    <div className="text-kit-2xs uppercase tracking-[0.14em] text-[var(--inyc-primary-text-color-light)]">
                      lineup
                    </div>
                    <div className="text-kit-xs text-[var(--inyc-primary-text-color-light)]">
                      accept, decline, or reopen slots here
                    </div>
                  </div>
                </div>
                <LineupManager
                  eventId={selectedParentEvent._id}
                  compact
                  initialOccurrenceStart={selectedCalendarProps.occurrenceStart}
                  onSlotsChange={handlePopupLineupChange}
                  contextRosterId={isRosterScope ? rosterId : undefined}
                />
              </div>
            )}
          </div>
        </>,
        document.body
      )}

      {composerData && createPortal(
        <>
          <div
            className="fixed inset-0 z-[9998] bg-scrim backdrop-blur-[6px]"
            onClick={requestCloseComposer}
          />
          <div className="fixed inset-x-0 bottom-0 z-[9999] max-h-[92vh] overflow-hidden rounded-t-kit-xl border-t-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary)] shadow-[0_-24px_80px_rgba(0,0,0,0.35)] md:inset-x-1/2 md:bottom-auto md:top-[4vh] md:w-[min(920px,calc(100vw_-_3rem))] md:-translate-x-1/2 md:rounded-kit-xl md:border-[1px]">
            <div className="sticky top-0 z-10 flex items-center justify-between gap-kit-16 border-b-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary)] px-kit-20 py-kit-16">
              <div className="min-w-0">
                <div className="font-['Primary-Bold'] text-kit-xs uppercase tracking-[0.16em] text-[var(--inyc-primary-text-color-light)]">
                  {composerTitle}
                </div>
                <div className="mt-kit-4 truncate font-['Primary-Bold'] text-kit-md lowercase text-[var(--inyc-primary-text-color)]">
                  {composerSummary}
                </div>
              </div>
              <KitButton variant="tertiary" size="sm" onClick={requestCloseComposer}>
                close
              </KitButton>
            </div>
            <div className="max-h-[calc(92vh_-_4.4rem)] overflow-auto px-kit-4 pb-kit-32 md:px-kit-16">
              <EventPostingSurface
                variant="modal"
                userLocation={userLocation}
                initialEventData={composerData}
                onClose={closeComposer}
                onDirtyChange={setComposerDirty}
                onDateChange={handleComposerDateChange}
                onSubmitted={onPostedEventCreated}
                lockedRosterId={isRosterScope && !editingEvent ? rosterId : undefined}
                lockedRosterName={isRosterScope && !editingEvent ? rosterName : undefined}
                showTemplateAction={!editingEvent}
                hideArtistTagging={isBookingsScope || isRosterScope}
              />
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
};

export default PostedEventsCalendar;
